import { Link } from "react-router-dom";
import { Zap, ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  const effectiveDate = "February 2026";
  const contactEmail = "support@postqik.com";
  const legalEntity = "VZV Ltd";

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="privacy-policy-page">
      <header className="border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="privacy-home-link">
            <span className="rounded-md bg-gradient-to-br from-fuchsia-600 to-cyan-600 p-1.5 text-white">
              <Zap className="w-4 h-4" />
            </span>
            <span className="font-display font-black tracking-tight text-slate-900">PostQik</span>
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
          Legal
        </div>
        <h1 className="mt-1 font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">
          Privacy Policy
        </h1>
        <p className="mt-2 text-sm text-slate-500">Effective date: {effectiveDate}</p>

        <div className="legal-content mt-8" data-testid="privacy-body">
          <p>
            This Privacy Policy explains how {legalEntity} (&ldquo;PostQik&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;),
            operator of the PostQik web application (the &ldquo;Service&rdquo;), collects, uses, stores and shares
            information when you use the Service. By using the Service you agree to this Policy.
          </p>

          <h2>1. Summary</h2>
          <ul>
            <li>We collect only what we need to sign you in and publish your content: your name, email, and the OAuth tokens you grant us.</li>
            <li>Files you select from Google Drive are streamed through our backend to the destination platform for the moment of publishing only. We do not store their contents.</li>
            <li>We do not sell your data, ever. We do not use it to train AI models.</li>
            <li>You can disconnect any integration or delete your account at any time to revoke our access.</li>
          </ul>

          <h2>2. Information We Collect</h2>

          <h3>2.1 Account information</h3>
          <p>When you register, we collect your name, email address, and a hashed password. If an admin approves your account, we record the approval status and any plan tier assigned to you.</p>

          <h3>2.2 Third-party integration tokens</h3>
          <p>When you connect Google Drive, TikTok, or Instagram via our publishing partner (Upload-Post), we receive OAuth tokens that let us act on your behalf. We store these tokens encrypted in our database, associated only with your account.</p>

          <h3>2.3 File metadata &amp; content (Google Drive)</h3>
          <p>When you browse the Drive Browser, we request file metadata (names, thumbnails, mime types, sizes, folder structure) from your Google Drive using the <code>drive.readonly</code> scope. When you click Send on a file, we temporarily stream that file&apos;s content through our backend to the destination platform.</p>

          <h3>2.4 Publishing activity</h3>
          <p>We log each send attempt: which file was sent, to which platform and account, at what time, and whether it succeeded. This is shown to you on the Logs page and is retained for troubleshooting and quota accounting.</p>

          <h3>2.5 Billing information</h3>
          <p>If you subscribe to a paid plan, our payment processor (Stripe) collects and stores your payment method. We never see or store your card details; we only receive a customer ID, subscription status, and invoice metadata from Stripe.</p>

          <h3>2.6 Technical information</h3>
          <p>Our servers automatically record standard request logs (IP address, user agent, endpoint, timestamp) used for security, abuse prevention, and debugging. We do not use third-party analytics or advertising trackers.</p>

          <h2>3. Google User Data — Limited Use Disclosure</h2>
          <p>
            PostQik&apos;s use of information received from Google APIs will adhere to the{" "}
            <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </p>
          <p>Specifically, in relation to the Google Drive data we access:</p>
          <ul>
            <li><strong>What we access.</strong> With your consent, we request the <code>https://www.googleapis.com/auth/drive.readonly</code> scope. This lets us list, view, and download files you own or have access to in Google Drive.</li>
            <li><strong>Why we access it.</strong> Solely to (a) show you a browsable file list in the Drive Browser inside PostQik and (b) send files you explicitly select to the social platforms you have connected.</li>
            <li><strong>We do not.</strong> Modify, delete, share, or transfer your Drive files to anyone other than the destination platform you selected for that specific send. We do not read, index, or process the contents of your files for any other purpose.</li>
            <li><strong>We do not use Google user data to train, fine-tune or evaluate any AI or machine-learning model.</strong></li>
            <li><strong>We do not sell Google user data.</strong> Ever.</li>
            <li><strong>Storage.</strong> The <em>content</em> of Drive files is not persisted on our servers; it is streamed through the backend and released from memory as soon as the send completes or fails. File metadata (name, id, thumbnail URL) may be cached briefly to power the Drive Browser and posting logs.</li>
            <li><strong>Access by humans.</strong> No PostQik employee, contractor, or admin can view the contents of your Drive files. Debugging is done using logs that never include file contents.</li>
            <li><strong>Revocation.</strong> You can disconnect Drive at any time from Integrations, or revoke access directly at{" "}
              <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">myaccount.google.com/permissions</a>. Revocation is immediate.
            </li>
          </ul>

          <h2>4. How We Use Your Information</h2>
          <ul>
            <li>To authenticate you and maintain your session.</li>
            <li>To fetch and display the files you have in Google Drive so you can select them.</li>
            <li>To send files you explicitly choose to TikTok and/or Instagram via our publishing partner.</li>
            <li>To enforce quotas, billing, and plan limits.</li>
            <li>To show you a history of your posting attempts.</li>
            <li>To detect and prevent abuse, spam, or violations of our Terms of Service.</li>
            <li>To communicate transactional messages (e.g. approval notice, billing receipts).</li>
          </ul>
          <p>We do not use your data for advertising, retargeting, or profile-building.</p>

          <h2>5. How We Share Information</h2>
          <p>We share information only with the following categories of third parties, and only to the minimum extent needed to run the Service:</p>
          <ul>
            <li><strong>Google (Drive API):</strong> we call Google&apos;s APIs on your behalf using the OAuth token you granted us.</li>
            <li><strong>Upload-Post</strong> (our publishing infrastructure partner): we send the file content and caption to Upload-Post so it can post to TikTok / Instagram on your behalf. See their privacy policy at <a href="https://upload-post.com" target="_blank" rel="noopener noreferrer">upload-post.com</a>.</li>
            <li><strong>Stripe</strong> (billing): payment processing for subscriptions.</li>
            <li><strong>Cloud hosting providers</strong> that host our servers and database.</li>
            <li><strong>Legal disclosure:</strong> we may disclose information when required by law, subpoena, or to protect the rights, property, or safety of PostQik, our users, or the public.</li>
          </ul>

          <h2>6. Data Retention</h2>
          <ul>
            <li><strong>Account data:</strong> retained while your account is active. Deleted (or anonymised) within 30 days of account deletion.</li>
            <li><strong>OAuth tokens:</strong> retained while the integration is connected. Deleted immediately on disconnect or account deletion.</li>
            <li><strong>Drive file contents:</strong> never persisted.</li>
            <li><strong>Post logs:</strong> retained for up to 12 months for troubleshooting and quota history.</li>
            <li><strong>Billing records:</strong> retained as required by applicable tax/accounting law (typically 7 years).</li>
          </ul>

          <h2>7. Security</h2>
          <p>We use industry-standard measures to protect your data: encrypted transport (HTTPS), password hashing (bcrypt), encrypted storage of OAuth tokens, principle-of-least-privilege access, and regular dependency patching. No system is 100% secure; you use the Service at your own risk.</p>

          <h2>8. Your Rights</h2>
          <p>Depending on where you live (e.g. EU/UK under GDPR, California under CCPA), you may have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you.</li>
            <li>Correct inaccurate data.</li>
            <li>Delete your data (&ldquo;right to be forgotten&rdquo;).</li>
            <li>Export your data in a portable format.</li>
            <li>Object to or restrict certain processing.</li>
            <li>Withdraw consent (by disconnecting integrations or deleting your account).</li>
          </ul>
          <p>To exercise any of these rights, contact us at <a href={`mailto:${contactEmail}`}>{contactEmail}</a>. We respond within 30 days.</p>

          <h2>9. Children</h2>
          <p>The Service is not directed to children under 13 (or under 16 in the EU/UK). We do not knowingly collect data from children. If you believe a child has provided us with information, contact us and we will delete it.</p>

          <h2>10. International Transfers</h2>
          <p>Our servers may be located outside your country of residence. By using the Service you consent to your data being transferred to, stored in, and processed in countries other than your own.</p>

          <h2>11. Changes to This Policy</h2>
          <p>We may update this Policy from time to time. Material changes will be announced by email or in-app notice at least 14 days before they take effect. Continued use of the Service after the effective date constitutes acceptance.</p>

          <h2>12. Contact</h2>
          <p>
            For any privacy question, request, or concern, contact us at{" "}
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
          </p>
        </div>

        <div className="mt-14 pt-8 border-t border-zinc-200 flex items-center justify-between text-xs text-slate-500">
          <Link to="/tos" className="hover:text-slate-900" data-testid="privacy-to-tos-link">
            Terms of Service
          </Link>
          <Link to="/login" className="hover:text-slate-900">
            Sign in
          </Link>
        </div>
      </main>
    </div>
  );
}
